import { useEffect, useMemo, useState } from "react";
import { apiClient } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Bell, AlertTriangle, Info, AlertCircle, Clock } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";

const LS_PREFIX = "snooze_alerts_v1";
const alertKey = (a) => `${a.type}::${a.ref || "_"}`;

// Snooze duration options (label + how to compute expiry epoch-ms)
const nextTomorrow8am = () => {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  d.setHours(8, 0, 0, 0);
  return d.getTime();
};
const nextMonday8am = () => {
  const d = new Date();
  const day = d.getDay(); // 0=Sun..6=Sat
  const add = ((1 - day + 7) % 7) || 7; // days until next Monday (never today)
  d.setDate(d.getDate() + add);
  d.setHours(8, 0, 0, 0);
  return d.getTime();
};
const SNOOZE_OPTIONS = [
  { key: "1h", label: "1 hour", compute: () => Date.now() + 60 * 60 * 1000 },
  { key: "4h", label: "4 hours", compute: () => Date.now() + 4 * 60 * 60 * 1000 },
  { key: "tomorrow", label: "Until tomorrow 8am", compute: nextTomorrow8am },
  { key: "24h", label: "24 hours", compute: () => Date.now() + 24 * 60 * 60 * 1000 },
  { key: "monday", label: "Until next Monday 8am", compute: nextMonday8am },
];

const getUserKey = () => {
  try {
    const raw = localStorage.getItem("user");
    const u = raw ? JSON.parse(raw) : null;
    return u?.id || u?.email || "anon";
  } catch {
    return "anon";
  }
};

const loadSnoozeMap = () => {
  try {
    const raw = localStorage.getItem(`${LS_PREFIX}_${getUserKey()}`);
    if (!raw) return {};
    const map = JSON.parse(raw);
    const now = Date.now();
    // Purge expired entries on read
    const cleaned = {};
    Object.entries(map).forEach(([k, v]) => {
      if (typeof v === "number" && v > now) cleaned[k] = v;
    });
    return cleaned;
  } catch {
    return {};
  }
};

const writeSnoozeMap = (map) => {
  try {
    localStorage.setItem(`${LS_PREFIX}_${getUserKey()}`, JSON.stringify(map));
  } catch {
    /* ignore */
  }
};

const SEV_CONFIG = {
  red: {
    bg: "bg-red-50",
    border: "border-red-200",
    hover: "hover:bg-red-100",
    text: "text-red-700",
    pill: "bg-red-100 text-red-700",
    icon: AlertCircle,
    label: "Urgent",
  },
  amber: {
    bg: "bg-amber-50",
    border: "border-amber-200",
    hover: "hover:bg-amber-100",
    text: "text-amber-700",
    pill: "bg-amber-100 text-amber-700",
    icon: AlertTriangle,
    label: "Watch",
  },
  blue: {
    bg: "bg-blue-50",
    border: "border-blue-200",
    hover: "hover:bg-blue-100",
    text: "text-blue-700",
    pill: "bg-blue-100 text-blue-700",
    icon: Info,
    label: "Info",
  },
};

export default function OwnerAlerts({ onNavigate, onOpenFundraiser }) {
  const [loading, setLoading] = useState(false);
  const [alerts, setAlerts] = useState([]);
  const [snoozeMap, setSnoozeMap] = useState(() => loadSnoozeMap());

  useEffect(() => {
    setLoading(true);
    apiClient
      .get("/alerts")
      .then((res) => setAlerts(res.data?.alerts || []))
      .catch(() => setAlerts([]))
      .finally(() => setLoading(false));
  }, []);

  const visibleAlerts = useMemo(() => {
    const now = Date.now();
    return alerts.filter((a) => {
      const until = snoozeMap[alertKey(a)];
      return !(until && until > now);
    });
  }, [alerts, snoozeMap]);

  const snoozeCount = alerts.length - visibleAlerts.length;

  const handleSnooze = (a, expiryMs) => {
    if (!expiryMs) return;
    const next = { ...snoozeMap, [alertKey(a)]: expiryMs };
    setSnoozeMap(next);
    writeSnoozeMap(next);
  };

  const handleUnsnoozeAll = () => {
    setSnoozeMap({});
    writeSnoozeMap({});
  };

  const counts = visibleAlerts.reduce(
    (acc, a) => ({ ...acc, [a.severity]: (acc[a.severity] || 0) + 1 }),
    {}
  );

  return (
    <Card className="border-slate-200" data-testid="owner-alerts-card">
      <CardHeader>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="flex items-center gap-2">
            <Bell className="h-5 w-5 text-slate-700" />
            Alerts
            {visibleAlerts.length > 0 && (
              <span className="ml-1 px-2 py-0.5 text-xs rounded-full bg-slate-100 text-slate-600">
                {visibleAlerts.length}
              </span>
            )}
            {snoozeCount > 0 && (
              <span
                className="ml-1 px-2 py-0.5 text-xs rounded-full bg-slate-50 text-slate-400 inline-flex items-center gap-1"
                data-testid="snoozed-count"
                title={`${snoozeCount} alert${snoozeCount === 1 ? "" : "s"} currently snoozed`}
              >
                <Clock className="h-3 w-3" />
                {snoozeCount} snoozed
                <button
                  type="button"
                  onClick={handleUnsnoozeAll}
                  className="ml-1.5 text-blue-600 hover:text-blue-800 underline decoration-dotted"
                  data-testid="unsnooze-all-btn"
                >
                  Unsnooze all
                </button>
              </span>
            )}
          </CardTitle>
          <div className="flex gap-2 text-xs" data-testid="alerts-severity-summary">
            {counts.red > 0 && (
              <span className="px-2 py-0.5 rounded-full bg-red-100 text-red-700 font-medium">
                {counts.red} urgent
              </span>
            )}
            {counts.amber > 0 && (
              <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 font-medium">
                {counts.amber} watch
              </span>
            )}
            {counts.blue > 0 && (
              <span className="px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 font-medium">
                {counts.blue} info
              </span>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {loading && <div className="text-center py-6 text-slate-400">Checking…</div>}
        {!loading && visibleAlerts.length === 0 && (
          <div className="text-center py-6 text-slate-400" data-testid="alerts-empty">
            ✅ {snoozeCount > 0 ? "No active alerts (others snoozed)." : "No active alerts. All clear."}
          </div>
        )}
        {!loading && visibleAlerts.length > 0 && (
          <div className="space-y-2" data-testid="alerts-list">
            {visibleAlerts.map((a, idx) => {
              const cfg = SEV_CONFIG[a.severity] || SEV_CONFIG.blue;
              const Icon = cfg.icon;
              // Routing per alert type
              const handleClick = () => {
                switch (a.type) {
                  case "fundraiser_no_signups":
                  case "no_activity_by_noon":
                    if (a.ref && onOpenFundraiser) onOpenFundraiser(a.ref);
                    else if (onNavigate) onNavigate("team");
                    break;
                  case "cancellation_spike":
                    if (onNavigate) onNavigate("cancellations");
                    break;
                  case "no_coverage_tomorrow":
                    if (onNavigate) onNavigate("schedule");
                    break;
                  case "bottom_location":
                    if (onNavigate) onNavigate("activity-locations");
                    break;
                  default:
                    break;
                }
              };
              return (
                <div
                  key={`${a.type}-${a.ref || idx}`}
                  onClick={handleClick}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") handleClick();
                  }}
                  className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${cfg.bg} ${cfg.border} ${cfg.hover}`}
                  data-testid={`alert-${a.type}-${idx}`}
                >
                  <Icon className={`h-4 w-4 mt-0.5 flex-shrink-0 ${cfg.text}`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${cfg.pill}`}>
                        {cfg.label}
                      </span>
                      <span className="text-sm text-slate-800">{a.message}</span>
                    </div>
                    {a.date && (
                      <div className="text-xs text-slate-400 mt-0.5">{a.date}</div>
                    )}
                  </div>
                  <div onClick={(e) => e.stopPropagation()} className="ml-2 flex-shrink-0">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button
                          type="button"
                          title="Snooze"
                          aria-label="Snooze alert"
                          className="p-1.5 rounded-md text-slate-400 hover:text-slate-700 hover:bg-white/70 transition-colors"
                          data-testid={`alert-snooze-${a.type}-${idx}`}
                        >
                          <Clock className="h-4 w-4" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" data-testid={`alert-snooze-menu-${a.type}-${idx}`}>
                        <DropdownMenuLabel className="text-xs text-slate-500">Snooze for…</DropdownMenuLabel>
                        <DropdownMenuSeparator />
                        {SNOOZE_OPTIONS.map((opt) => (
                          <DropdownMenuItem
                            key={opt.key}
                            onSelect={() => handleSnooze(a, opt.compute())}
                            data-testid={`snooze-opt-${opt.key}-${idx}`}
                          >
                            {opt.label}
                          </DropdownMenuItem>
                        ))}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
